import Contracts from '@tokenysolutions/t-rex';
import { Contract, providers, Signer } from 'ethers';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

type TransactionResponse = providers.TransactionResponse;

/**
 * Deploys TrustedIssuersRegistry to the Ethereum's Blockchain
 * @param {{signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>}
 */
export async function deployTrustedIssuersRegistry(contract: { signer: Signer; overrides: OverrideTransactionsInterface }): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.TrustedIssuersRegistry.abi,
    bytecode: Contracts.contracts.TrustedIssuersRegistry.bytecode,
    signer: contract.signer,
  });
  const deploy = await factory.deploy(contract.overrides);
  return await deploy.deployed();
}

/**
 * registers a ClaimIssuer contract as trusted claim issuer.
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function addTrustedIssuer(tx: {
  trustedIssuer: string;
  claimTopics: number[];
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.addTrustedIssuer(tx.trustedIssuer, tx.claimTopics, tx.overrides);
}

/**
 * Removes the ClaimIssuer contract of a trusted claim issuer.
 * @param {{trustedIssuer: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function removeTrustedIssuer(tx: {
  trustedIssuer: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.removeTrustedIssuer(tx.trustedIssuer, tx.overrides);
}

/**
 * Gets all the trusted claim issuers stored.
 * @param {{registry: Contract}} tx
 * @returns {Promise<string[]>}
 */
export async function getTrustedIssuers(tx: { registry: Contract }): Promise<string[]> {
  return await tx.registry.getTrustedIssuers();
}

/**
 * Checks if the ClaimIssuer contract is trusted.
 * @param {{issuerAddress: string; registry: Contract}} tx
 * @returns {Promise<boolean>}
 */
export async function isTrustedIssuer(tx: { issuerAddress: string; registry: Contract }): Promise<boolean> {
  return await tx.registry.isTrustedIssuer(tx.issuerAddress);
}

/**
 * Gets all the claim topics of trusted claim issuer
 * @param {{issuerAddress: string; registry: Contract}} tx
 * @returns {Promise<number[]>}
 */
export async function getTrustedIssuerClaimTopics(tx: { issuerAddress: string; registry: Contract }): Promise<number[]> {
  return await tx.registry.getTrustedIssuerClaimTopics(tx.issuerAddress);
}

/**
 * Checks if the trusted claim issuer is allowed to emit a certain claim topic
 * @param {{issuerAddress: string; claimTopic: number; registry: Contract}} tx
 * @returns {Promise<boolean>}
 */
export async function hasClaimTopic(tx: { issuerAddress: string; claimTopic: number; registry: Contract }): Promise<boolean> {
  return await tx.registry.hasClaimTopic(tx.issuerAddress, tx.claimTopic);
}

/**
 * Updates the set of claim topics that a trusted issuer is allowed to emit.
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function updateIssuerClaimTopics(tx: {
  claimIssuer: string;
  claimTopics: number[];
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.updateIssuerClaimTopics(tx.claimIssuer, tx.claimTopics, tx.overrides);
}

/**
 * Transfers the Ownership of the Trusted Issuers Registry to a new Owner.
 * @param {{newOwner: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferTrustedIssuersRegistryOwnership(tx: {
  newOwner: string;
  registry: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.registry.transferOwnershipOnIssuersRegistryContract(tx.newOwner, tx.overrides);
}

/**
 * Returns the Owner of the TrustedIssuers Registry.
 * @param {{registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfTrustedIssuersRegistry(tx: { registry: Contract }): Promise<string> {
  return await tx.registry.owner();
}
